// src/pages/ContrattiManutenzionePage.js
// Modulo Contratti di Manutenzione — semplice a colonne, come richiesto: tiene sotto
// controllo data inizio/scadenza contratto, data disdetta (calcolata da scadenza - preavviso,
// ma sovrascrivibile), tempi di intervento straordinaria e costi feriale/festivo/notturno.
//
// Due viste sulla stessa collection `contrattiManutenzione` (nessuna doppia scrittura):
// - Gestione (Titolare/Gestione aziendale/Amministrazione/PM): unico punto di creazione e
//   modifica, su tutti i contratti dell'organizzazione — la scadenza/disdetta di un
//   contratto di manutenzione non può dipendere da una sola persona, per questo è una
//   sezione di gestione condivisa, non un archivio personale del singolo PM.
// - Personale (PM): sola consultazione, filtrata sulle commesse di cui l'utente è
//   responsabile — un pannello di controllo scadenze, non un secondo punto di inserimento.
//
// Un contratto è sempre agganciato a una commessa: o una già esistente (il lavoro
// originale, caso più raro), o una creata apposta e dedicata solo alla manutenzione
// (caso comune) — cliente, codice commessa e responsabile vengono presi automaticamente
// da lì al momento della creazione, mai ritoccati a mano — evita la doppia digitazione
// di dati che esistono già in Nexiora.
import React, { useState, useEffect, useRef } from "react";
import { collection, query, where, onSnapshot, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { messaggioErrore } from "../utils/erroriUtente";
import { logModifica } from "../utils/logModifica";
import { useAuth } from "../contexts/AuthContext";
import { useCanSee } from "../hooks/useCanSee";
import { useOrg } from "../contexts/OrgContext";
import { useToast } from "../utils/Toast";
import { confirm } from "../utils/confirm";
import { Wrench, Plus, Check, Trash2, Bell } from "lucide-react";
import { toLocalDateStr } from "../utils/dateUtils";
import NewProjectModal from "./NewProjectModal";
import { numIt } from "../utils/economiaCommessa";
import { formatValutaItaliana } from "../utils/clienteHelpers";

// ── Calcolo automatico della data di disdetta (scadenza - N mesi) ──────────────
function calcolaDataDisdetta(dataFine, mesiPreavviso) {
  if (!dataFine || !mesiPreavviso) return "";
  const d = new Date(dataFine + "T00:00:00");
  d.setMonth(d.getMonth() - parseInt(mesiPreavviso, 10));
  return toLocalDateStr(d);
}

function giorniA(dataStr) {
  if (!dataStr) return null;
  const oggi = new Date(); oggi.setHours(0,0,0,0);
  const d = new Date(dataStr + "T00:00:00");
  return Math.round((d - oggi) / 86400000);
}

const inp = { border:"1px solid #e5e7eb", borderRadius:7, padding:"7px 10px", fontSize:13, fontFamily:"'Inter',sans-serif", outline:"none", width:"100%", boxSizing:"border-box" };
const lbl = { fontSize:11, fontWeight:600, color:"#6b7280", display:"block", marginBottom:4 };

// ── Cella cliccabile a editing inline ───────────────────────────────────────────
function CellaInline({ value, type="text", onSave, onCancel, placeholder="" }) {
  const [val, setVal] = useState(value ?? "");
  const ref = useRef();
  useEffect(()=>{ ref.current?.focus(); },[]);
  const salva = () => onSave(val);
  const key = e => { if (e.key==="Enter") salva(); if (e.key==="Escape") onCancel(); };
  return (
    <div style={{display:"flex",gap:3,alignItems:"center"}}>
      <input ref={ref} type={type} value={val} onChange={e=>setVal(e.target.value)}
        onBlur={salva} onKeyDown={key} placeholder={placeholder}
        style={{flex:1,padding:"3px 5px",border:"2px solid #7c3aed",borderRadius:5,fontSize:12,fontFamily:"'Inter',sans-serif",outline:"none",minWidth:0}}/>
      <button onMouseDown={e=>{e.preventDefault();salva();}}
        style={{background:"#7c3aed",border:"none",borderRadius:4,padding:"3px 5px",cursor:"pointer",color:"#fff",flexShrink:0}}>
        <Check size={10}/>
      </button>
    </div>
  );
}

function Cella({ value, display, canEdit, onEdit, placeholder="—", colore }) {
  return (
    <div onClick={canEdit?onEdit:undefined}
      style={{padding:"5px 6px",minHeight:26,cursor:canEdit?"pointer":"default",
        color:colore||(value?"#111827":"#d1d5db"),fontSize:12,borderRadius:4,fontWeight:colore?700:400,whiteSpace:"nowrap"}}
      onMouseOver={e=>{if(canEdit)e.currentTarget.style.background="#f3f4f6";}}
      onMouseOut={e=>{e.currentTarget.style.background="transparent";}}>
      {display ?? value ?? placeholder}
    </div>
  );
}

// ── Form di aggiunta: sceglie una commessa esistente e aggancia il contratto ────
function FormNuovoContratto({ commesseDisponibili, orgId, currentUser, onSalva, onAnnulla }) {
  const [search, setSearch] = useState("");
  const [modalitaDedicata, setModalitaDedicata] = useState(false);
  const filtrate = commesseDisponibili.filter(c =>
    !search || c.name?.toLowerCase().includes(search.toLowerCase()) || (c.codiceCommessa||"").toLowerCase().includes(search.toLowerCase())
  ).sort((a,b)=>a.name?.localeCompare(b.name||"","it"));

  if (modalitaDedicata) {
    // Riusa la stessa identica finestra di creazione commessa già presente in tutta l'app
    // (con tanto di casella "questa è la manutenzione di un'altra commessa", facoltativa,
    // già pronta lì per chi ha davvero un lavoro originale a cui collegarla) — invece di
    // costruire un secondo modo di creare una commessa, solo per questo caso.
    return <NewProjectModal onClose={()=>setModalitaDedicata(false)} onCreated={(nuovaCommessa)=>{ setModalitaDedicata(false); onSalva(nuovaCommessa); }} prefill={{manutenzioneCommessa:true}}/>;
  }

  return (
    <div style={{background:"#faf5ff",border:"1px solid #e9d5ff",borderRadius:10,padding:14,marginBottom:14}}>
      <div style={{fontSize:12,fontWeight:700,color:"#7c3aed",marginBottom:8}}>➕ Nuovo contratto di manutenzione</div>
      <div style={{display:"flex",gap:8,marginBottom:10}}>
        <button className="btn btn-secondary" style={{flex:1,fontSize:12}} onClick={()=>setModalitaDedicata(true)}>
          🆕 Crea una commessa dedicata al cliente
        </button>
      </div>
      <div style={{fontSize:11,color:"#9ca3af",textAlign:"center",margin:"6px 0"}}>oppure, il caso più raro:</div>
      <input style={inp} value={search} onChange={e=>setSearch(e.target.value)} placeholder="🔍 Collega a una commessa già esistente..."/>
      {search && (
        <div style={{border:"1px solid #e5e7eb",borderRadius:8,maxHeight:180,overflowY:"auto",background:"#fff",marginTop:8}}>
          {filtrate.length===0 ? (
            <div style={{padding:"12px 14px",color:"#9ca3af",fontSize:13}}>Nessuna commessa trovata (o già ha un contratto registrato)</div>
          ) : filtrate.map(c => (
            <div key={c.id} onClick={()=>onSalva(c)}
              style={{padding:"9px 14px",cursor:"pointer",fontSize:13,borderBottom:"1px solid #f3f4f6",fontWeight:500}}
              onMouseOver={e=>e.currentTarget.style.background="#f9f5ff"} onMouseOut={e=>e.currentTarget.style.background="transparent"}>
              {c.codiceCommessa && <span style={{color:"#7c3aed",fontFamily:"monospace",marginRight:6}}>[{c.codiceCommessa}]</span>}
              {c.name}
              {c.responsabileNome && <span style={{color:"#9ca3af",marginLeft:8,fontSize:11}}>· PM: {c.responsabileNome}</span>}
            </div>
          ))}
        </div>
      )}
      <div style={{marginTop:10}}>
        <button className="btn btn-secondary" onClick={onAnnulla}>Annulla</button>
      </div>
    </div>
  );
}

// ── Tabella condivisa tra vista Personale e vista Registro ─────────────────────
function TabellaContratti({ contratti, canEdit, onAggiorna, onElimina }) {
  const [cellAttiva, setCellAttiva] = useState(null); // {id, campo}

  const COLONNE = [
    { key:"projectName",      label:"Commessa",          width:170, sola:true },
    { key:"clienteNome",      label:"Cliente",           width:140, sola:true },
    { key:"responsabileNome", label:"PM",                width:110, sola:true },
    { key:"dataInizio",       label:"Data inizio",       width:110, type:"date" },
    { key:"dataFine",         label:"Data scadenza",     width:110, type:"date" },
    { key:"mesiPreavviso",    label:"Preavviso (mesi)",  width:90,  type:"number" },
    { key:"dataDisdetta",     label:"Disdetta entro",    width:110, type:"date" },
    { key:"tempoInterventoFeriale",  label:"SLA feriale (ore)",  width:110, type:"number" },
    { key:"tempoInterventoFestivo",  label:"SLA festivo (ore)",  width:110, type:"number" },
    { key:"tempoInterventoNotturno", label:"SLA notturno (ore)", width:110, type:"number" },
    { key:"costoFeriale",     label:"Costo feriale €",   width:110, type:"number" },
    { key:"costoFestivo",     label:"Costo festivo €",   width:110, type:"number" },
    { key:"costoNotturno",    label:"Costo notturno €",  width:110, type:"number" },
    { key:"note",             label:"Note",              width:160, type:"text" },
  ];

  const aggiornaCampo = (c, campo, valore) => {
    setCellAttiva(null);
    const patch = { [campo]: valore };
    // La data disdetta si ricalcola da sola quando cambiano scadenza o preavviso, a meno
    // che l'utente non l'abbia già personalizzata a mano (dataDisdettaManuale=true).
    if ((campo==="dataFine" || campo==="mesiPreavviso") && !c.dataDisdettaManuale) {
      const nuovaFine = campo==="dataFine" ? valore : c.dataFine;
      const nuovoPreavviso = campo==="mesiPreavviso" ? valore : c.mesiPreavviso;
      patch.dataDisdetta = calcolaDataDisdetta(nuovaFine, nuovoPreavviso);
    }
    if (campo==="dataDisdetta") patch.dataDisdettaManuale = true;
    onAggiorna(c, patch);
  };

  if (contratti.length===0) {
    return <div style={{textAlign:"center",padding:40,color:"#9ca3af",fontSize:13}}>Nessun contratto di manutenzione registrato.</div>;
  }

  return (
    <div style={{overflowX:"auto",borderRadius:12,border:"1px solid #e5e7eb"}}>
      <table style={{borderCollapse:"collapse",fontSize:12,minWidth:1500,width:"100%"}}>
        <thead>
          <tr style={{background:"#f9fafb",borderBottom:"2px solid #e5e7eb"}}>
            {COLONNE.map(col=><th key={col.key} style={{padding:"8px 10px",textAlign:"left",fontSize:11,fontWeight:700,color:"#6b7280",whiteSpace:"nowrap",minWidth:col.width}}>{col.label}</th>)}
            <th style={{width:60}}>🔔</th>
            {canEdit && <th style={{width:30}}/>}
          </tr>
        </thead>
        <tbody>
          {contratti.map(c=>{
            const gScadenza = giorniA(c.dataFine);
            const gDisdetta = giorniA(c.dataDisdetta);
            const disdettaUrgente = gDisdetta!==null && gDisdetta<=(c.promemoriaGiorni||60) && gDisdetta>=0;
            const disdettaScaduta = gDisdetta!==null && gDisdetta<0 && gScadenza!==null && gScadenza>=0;
            return (
              <tr key={c.id} style={{
                borderBottom:"1px solid #f3f4f6",
                background: disdettaScaduta ? "#fef2f2" : disdettaUrgente ? "#fffbeb" : "#fff",
              }}>
                {COLONNE.map(col=>{
                  const isAtt = cellAttiva?.id===c.id && cellAttiva?.campo===col.key;
                  if (col.sola) return (
                    <td key={col.key} style={{padding:"5px 6px",fontSize:12,color:col.key==="projectName"?"#7c3aed":"#374151",fontWeight:col.key==="projectName"?700:400}}>
                      {col.key==="projectName" && c.codiceCommessa && <span style={{fontFamily:"monospace",marginRight:4}}>[{c.codiceCommessa}]</span>}
                      {c[col.key]||"—"}
                    </td>
                  );
                  let colore;
                  if (col.key==="dataDisdetta" && disdettaScaduta) colore = "#dc2626";
                  else if (col.key==="dataDisdetta" && disdettaUrgente) colore = "#d97706";
                  return (
                    <td key={col.key} style={{padding:0}}>
                      {isAtt ? (
                        <CellaInline value={c[col.key]} type={col.type} onSave={v=>aggiornaCampo(c,col.key,col.type==="number"?numIt(v):v)} onCancel={()=>setCellAttiva(null)}/>
                      ) : (
                        <Cella value={c[col.key]} display={col.type==="date" && c[col.key] ? String(c[col.key]).split("-").reverse().join("/") : undefined}
                          canEdit={canEdit} colore={colore} onEdit={()=>canEdit&&setCellAttiva({id:c.id,campo:col.key})}/>
                      )}
                    </td>
                  );
                })}
                <td style={{padding:"5px 6px",textAlign:"center"}}>
                  {disdettaScaduta ? <span title="Termine per la disdetta superato" style={{color:"#dc2626"}}><Bell size={13}/></span> :
                   disdettaUrgente ? <span title={`Disdetta entro ${gDisdetta} giorni`} style={{color:"#d97706"}}><Bell size={13}/></span> :
                   <span style={{color:"#d1d5db"}}>—</span>}
                </td>
                {canEdit && (
                  <td style={{padding:"5px 6px",textAlign:"center"}}>
                    <button onClick={()=>onElimina(c)} style={{background:"none",border:"none",cursor:"pointer",color:"#fca5a5"}}><Trash2 size={13}/></button>
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ── Motore condiviso: carica commesse (filtrate per scope) + contratti, espone azioni ──
function useContrattiManutenzione({ scope }) {
  const { currentUser } = useAuth();
  const { effectiveOrgId } = useOrg();
  const { addToast } = useToast();
  const [commesse, setCommesse] = useState([]);
  const [contratti, setContratti] = useState([]);

  useEffect(() => {
    if (!effectiveOrgId) return;
    const base = [collection(db,"projects"), where("organizationId","==",effectiveOrgId)];
    const q = scope==="mie" && currentUser?.uid
      ? query(...base, where("responsabileUid","==",currentUser.uid))
      : query(...base);
    return onSnapshot(q, snap => setCommesse(snap.docs.map(d=>({id:d.id,...d.data()})).filter(p=>!p.isParent && !p.archived)));
  }, [effectiveOrgId, scope, currentUser?.uid]);

  useEffect(() => {
    if (!effectiveOrgId) return;
    return onSnapshot(query(collection(db,"contrattiManutenzione"), where("orgId","==",effectiveOrgId)),
      snap => setContratti(snap.docs.map(d=>({id:d.id,...d.data()}))));
  }, [effectiveOrgId]);

  // Nella vista personale, solo i contratti delle proprie commesse; nel registro, tutti.
  const contrattiVisibili = (scope==="mie"
    ? contratti.filter(c => commesse.some(p=>p.id===c.projectId))
    : contratti
  ).sort((a,b) => (a.dataDisdetta||"9999").localeCompare(b.dataDisdetta||"9999"));

  const commesseDisponibili = commesse.filter(p => !contratti.some(c=>c.projectId===p.id));

  const creaContratto = async (commessa) => {
    try {
      // Un contratto per commessa, sempre. Il controllo legge direttamente dal database —
      // non dalla lista a schermo, che subito dopo una creazione potrebbe non essersi
      // ancora aggiornata. Serve perché la finestra di creazione commessa (spuntando
      // "È una commessa di manutenzione") crea già da sola il contratto: senza questo
      // controllo, tornando qui veniva creato un secondo contratto identico, senza che
      // nessuno avesse premuto nulla.
      const giaEsistente = await getDocs(query(collection(db,"contrattiManutenzione"),
        where("orgId","==",effectiveOrgId), where("projectId","==",commessa.id)));
      if (!giaEsistente.empty) {
        addToast("Contratto di manutenzione già presente per questa commessa ✅","success");
        return;
      }
      const nuovoRef = await addDoc(collection(db,"contrattiManutenzione"), {
        orgId: effectiveOrgId,
        projectId: commessa.id, projectName: commessa.name, codiceCommessa: commessa.codiceCommessa||"",
        clienteId: commessa.clienteId||"", clienteNome: commessa.clienteNome||"",
        responsabileUid: commessa.responsabileUid||"", responsabileNome: commessa.responsabileNome||"",
        dataInizio: "", dataFine: "", mesiPreavviso: 6, dataDisdetta: "", dataDisdettaManuale: false,
        tempoInterventoFeriale: "", tempoInterventoFestivo: "", tempoInterventoNotturno: "",
        costoFeriale: 0, costoFestivo: 0, costoNotturno: 0,
        // Dove finisce il ricavo di ogni intervento — di default sulla stessa commessa del
        // contratto (il caso comune quando la manutenzione nasce come commessa dedicata fin
        // da subito); "commessaSeparata" per il caso raro in cui il contratto vive ancora
        // dentro il lavoro originale ma i ricavi della manutenzione vanno tenuti separati.
        destinazioneRicavi: "stessaCommessa", commessaRicaviId: "", commessaRicaviNome: "",
        promemoria: true, promemoriaGiorni: 60, note: "",
        creatoDaUid: currentUser?.uid, creatoDaNome: currentUser?.displayName||"",
        createdAt: serverTimestamp(), updatedAt: serverTimestamp(),
      });
      await logModifica({
        orgId: effectiveOrgId, uid: currentUser?.uid, nomeUtente: currentUser?.displayName||"",
        sezione: "contratti-manutenzione", refId: nuovoRef.id, refNome: commessa.name,
        campo: "creazione", campoLabel: "Contratto di manutenzione creato",
        before: "—", after: commessa.name,
      });
      addToast("Contratto di manutenzione creato ✅","success");
    } catch(err) { addToast(messaggioErrore(err,"creare il contratto"),"error"); }
  };

  const aggiornaContratto = async (contratto, patch) => {
    try {
      await updateDoc(doc(db,"contrattiManutenzione",contratto.id), {
        ...patch, updatedAt: serverTimestamp(),
        ultimaModificaUid: currentUser?.uid, ultimaModificaNome: currentUser?.displayName||"",
      });
    } catch(err) { addToast(messaggioErrore(err,"aggiornare il contratto"),"error"); }
  };

  const eliminaContratto = async (contratto) => {
    if (!await confirm(`Eliminare il contratto di manutenzione per "${contratto.projectName}"?`,{type:"danger"})) return;
    try {
      await deleteDoc(doc(db,"contrattiManutenzione",contratto.id));
      addToast("Contratto eliminato","info");
    } catch(err) { addToast(messaggioErrore(err,"eliminare il contratto"),"error"); }
  };

  return { commesseDisponibili, contrattiVisibili, creaContratto, aggiornaContratto, eliminaContratto };
}

// ── Cruscotto — riepilogo per il PM sulle proprie commesse di manutenzione:
// ricavi del mese, rispetto SLA recente, contratti vicini a scadenza. Tutto calcolato dal
// vivo sulle stesse collezioni già in uso altrove — nessun numero salvato a parte che
// rischi di disallinearsi.
function CruscottoManutenzionePM({ contratti }) {
  // Serve per filtrare le letture per organizzazione (Regole Firestore): in W306 il filtro era
  // stato aggiunto ma la variabile non esisteva in questo componente → errore al primo render.
  const { effectiveOrgId } = useOrg();
  const [ricaviMese, setRicaviMese] = useState(0);
  const [slaOk, setSlaOk] = useState(0);
  const [slaKo, setSlaKo] = useState(0);

  const projectIds = contratti.map(c=>c.projectId).filter(Boolean);
  // Firestore "in" accetta al massimo 10 valori — con più contratti si interroga a blocchi.
  const blocchi = [];
  for (let i=0;i<projectIds.length;i+=10) blocchi.push(projectIds.slice(i,i+10));

  useEffect(()=>{
    if (!effectiveOrgId || blocchi.length===0) { setRicaviMese(0); setSlaOk(0); setSlaKo(0); return; }
    const oggi = new Date();
    const inizioMese = toLocalDateStr(new Date(oggi.getFullYear(), oggi.getMonth(), 1));
    const unSubs = [];
    let ricaviAcc = {};
    let slaAcc = {};
    blocchi.forEach((blocco, idx) => {
      unSubs.push(onSnapshot(query(collection(db,"movimentiEconomici"), where("organizationId","==",effectiveOrgId), where("projectId","in",blocco), where("categoriaId","==","manutenzione"), where("tipo","==","ricavo")), snap=>{
        ricaviAcc[idx] = snap.docs.reduce((s,d)=>{
          const data = d.data();
          const creata = data.creadaAt?.toDate ? toLocalDateStr(data.creadaAt.toDate()) : "";
          return creata>=inizioMese ? s+(numIt(data.importo)||0) : s;
        },0);
        setRicaviMese(Object.values(ricaviAcc).reduce((a,b)=>a+b,0));
      }));
      unSubs.push(onSnapshot(query(collection(db,"manutenzioneInterventi"), where("organizationId","==",effectiveOrgId), where("projectId","in",blocco), where("slaRispettato","==",true)), snap=>{
        slaAcc[idx] = { ...slaAcc[idx], ok: snap.size };
        setSlaOk(Object.values(slaAcc).reduce((a,b)=>a+(b.ok||0),0));
      }));
      unSubs.push(onSnapshot(query(collection(db,"manutenzioneInterventi"), where("organizationId","==",effectiveOrgId), where("projectId","in",blocco), where("slaRispettato","==",false)), snap=>{
        slaAcc[idx] = { ...slaAcc[idx], ko: snap.size };
        setSlaKo(Object.values(slaAcc).reduce((a,b)=>a+(b.ko||0),0));
      }));
    });
    return ()=>unSubs.forEach(u=>u());
  },[JSON.stringify(projectIds), effectiveOrgId]);

  const scadenzaEntro60 = contratti.filter(c=>{
    if (!c.dataDisdetta) return false;
    const g = Math.ceil((new Date(c.dataDisdetta+"T00:00:00")-new Date())/(1000*60*60*24));
    return g>=0 && g<=60;
  }).length;

  return (
    <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:12,marginBottom:20}}>
      <div style={{background:"#f0fdf4",border:"1px solid #bbf7d0",borderRadius:10,padding:14}}>
        <div style={{fontSize:11,fontWeight:700,color:"#059669",marginBottom:4}}>RICAVI MANUTENZIONE — QUESTO MESE</div>
        <div style={{fontSize:20,fontWeight:800,color:"#059669"}}>€ {formatValutaItaliana(ricaviMese)}</div>
      </div>
      <div style={{background:slaKo>0?"#fef2f2":"#f0fdf4",border:`1px solid ${slaKo>0?"#fecaca":"#bbf7d0"}`,borderRadius:10,padding:14}}>
        <div style={{fontSize:11,fontWeight:700,color:slaKo>0?"#dc2626":"#059669",marginBottom:4}}>RISPETTO SLA (INTERVENTI SU CHIAMATA)</div>
        <div style={{fontSize:20,fontWeight:800,color:slaKo>0?"#dc2626":"#059669"}}>{slaOk} {slaOk===1?"rispettato":"rispettati"}{slaKo>0?` · ${slaKo} ${slaKo===1?"sforato":"sforati"}`:""}</div>
      </div>
      <div style={{background:scadenzaEntro60>0?"#fffbeb":"#f9fafb",border:`1px solid ${scadenzaEntro60>0?"#fde68a":"#e5e7eb"}`,borderRadius:10,padding:14}}>
        <div style={{fontSize:11,fontWeight:700,color:scadenzaEntro60>0?"#d97706":"#6b7280",marginBottom:4}}>DISDETTA ENTRO 60 GIORNI</div>
        <div style={{fontSize:20,fontWeight:800,color:scadenzaEntro60>0?"#d97706":"#6b7280"}}>{scadenzaEntro60} contratt{scadenzaEntro60===1?"o":"i"}</div>
      </div>
    </div>
  );
}

// ── Vista Personale (PM) — sola consultazione, solo le proprie commesse ────────
// La creazione e la gestione dei contratti è centralizzata nel Registro (Gestione) —
// qui il PM trova solo un pannello di controllo scadenze, in lettura, sulle commesse di
// cui è responsabile, più le email di promemoria già attive lato Cloud Function.
export default function ContrattiManutenzionePage() {
  const { isOrgAdmin, isPlatformOwner } = useAuth();
  const canSee = useCanSee();
  const puoAccedere = isOrgAdmin || isPlatformOwner || canSee("contratti-manutenzione");
  const { contrattiVisibili } = useContrattiManutenzione({ scope:"mie" });

  if (!puoAccedere) {
    return <div style={{padding:40,textAlign:"center",color:"#9ca3af"}}>Non hai accesso a questa sezione.</div>;
  }

  return (
    <div style={{padding:24,maxWidth:1600,flex:1,overflowY:"auto"}}>
      <h2 style={{fontSize:18,fontWeight:800,display:"flex",alignItems:"center",gap:8,margin:0,marginBottom:4}}>
        <Wrench size={18} color="#7c3aed"/> Contratti di Manutenzione
      </h2>
      <p style={{fontSize:12,color:"#9ca3af",marginBottom:16}}>
        Scadenze contrattuali (inizio, scadenza, mesi di preavviso e termine per la disdetta) delle commesse di cui sei responsabile — in sola consultazione. Per creare o modificare un contratto vai a Contratti di Manutenzione, in Gestione.
      </p>

      <CruscottoManutenzionePM contratti={contrattiVisibili}/>
      <TabellaContratti contratti={contrattiVisibili} canEdit={false} onAggiorna={()=>{}} onElimina={()=>{}}/>
    </div>
  );
}

// ── Vista Gestione (Titolare/Gestione/Amministrazione/PM) — creazione e gestione, tutta l'organizzazione ─
export function GestioneContrattiManutenzionePage() {
  const { isOrgAdmin, isPlatformOwner } = useAuth();
  const canSee = useCanSee();
  const puoAccedere = isOrgAdmin || isPlatformOwner || canSee("gestione-contratti-manutenzione");
  const [mostraForm, setMostraForm] = useState(false);
  const { commesseDisponibili, contrattiVisibili, creaContratto, aggiornaContratto, eliminaContratto } = useContrattiManutenzione({ scope:"tutte" });

  if (!puoAccedere) {
    return <div style={{padding:40,textAlign:"center",color:"#9ca3af"}}>Non hai accesso a questa sezione.</div>;
  }

  const scadute = contrattiVisibili.filter(c => { const g=giorniA(c.dataDisdetta); return g!==null && g<0 && giorniA(c.dataFine)>=0; }).length;
  const urgenti = contrattiVisibili.filter(c => { const g=giorniA(c.dataDisdetta); return g!==null && g>=0 && g<=(c.promemoriaGiorni||60); }).length;

  return (
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>
      <div style={{background:"#fff",borderBottom:"1px solid #e5e7eb",padding:"16px 24px",flexShrink:0}}>
        <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:8}}>
          <div>
            <h2 style={{fontSize:18,fontWeight:800,color:"#1e1b4b",margin:0,display:"flex",alignItems:"center",gap:8}}>
              <Wrench size={18}/> Contratti di Manutenzione
            </h2>
            <p style={{fontSize:12,color:"#9ca3af",margin:"2px 0 0"}}>{contrattiVisibili.length} contratto/i — gestione centralizzata, aperta a Titolare, Gestione, Amministrazione e Project Manager</p>
          </div>
          {!mostraForm && (
            <button onClick={()=>setMostraForm(true)} className="btn btn-primary" style={{display:"flex",alignItems:"center",gap:5}}>
              <Plus size={14}/> Nuovo contratto
            </button>
          )}
        </div>
        {(scadute>0 || urgenti>0) && (
          <div style={{display:"flex",gap:10}}>
            {scadute>0 && <span style={{fontSize:11,fontWeight:700,color:"#dc2626",background:"#fef2f2",padding:"3px 10px",borderRadius:8}}>⚠️ {scadute} termine disdetta superato</span>}
            {urgenti>0 && <span style={{fontSize:11,fontWeight:700,color:"#d97706",background:"#fffbeb",padding:"3px 10px",borderRadius:8}}>🔔 {urgenti} in avvicinamento</span>}
          </div>
        )}
      </div>
      <div style={{flex:1,overflow:"auto",padding:20}}>
        {mostraForm && (
          <FormNuovoContratto commesseDisponibili={commesseDisponibili} onAnnulla={()=>setMostraForm(false)}
            onSalva={async (c)=>{ await creaContratto(c); setMostraForm(false); }}/>
        )}
        <TabellaContratti contratti={contrattiVisibili} canEdit={true} onAggiorna={aggiornaContratto} onElimina={eliminaContratto}/>
      </div>
    </div>
  );
}
